/**
 * BulkImportModal — paste a list of handles or addresses and create one source per line.
 * Demo page: `/form-layouts` (stacked fields, one submit). Needs CanManageAgents (the caller gates it).
 */
import React, { useEffect, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { CheckCircle, Upload } from 'lucide-react';
import { z } from 'zod';
import type { BulkCreateIntelSourcesResponse } from '../../../generated/api/types.gen.js';
import { zIntelSourceCreate } from '../../../generated/api/zod.gen.js';
import { bulkCreateIntelSourcesMutation } from '../../../generated/api/@tanstack/react-query.gen.js';
import { CATEGORY_LABELS } from '../../../types/intel-source.js';
import type { IntelSourceCategory } from '../../../types/intel-source.js';
import { Modal, Button } from '../../../components/ui';
import { Form, SelectFormField, SubmitButton, TextAreaFormField, useZodForm } from '../../../components/form';
import { ALL_PLATFORMS } from './platform-catalog.js';

interface BulkImportModalProps {
  open: boolean;
  onClose: () => void;
  onImported: () => void;
}

const CATEGORY_OPTIONS = (Object.keys(CATEGORY_LABELS) as IntelSourceCategory[]).map((value) => ({
  value,
  label: CATEGORY_LABELS[value],
}));

// Platform and category come from the generated create schema; the pasted list is UI-only and is split on submit.
const importSchema = zIntelSourceCreate
  .pick({ platform: true, category: true })
  .extend({ handles: z.string().trim().min(1) });

/** One handle per non-empty line, with a leading @ removed. */
function parseHandles(raw: string): string[] {
  return raw
    .split('\n')
    .map((line) => line.trim().replace(/^@/, ''))
    .filter(Boolean);
}

function ImportForm({ onImported, onDone }: { onImported: () => void; onDone: () => void }) {
  const form = useZodForm(importSchema, { defaultValues: { platform: 'twitter', category: 'social', handles: '' } });
  const mutation = useMutation(bulkCreateIntelSourcesMutation());
  const [result, setResult] = useState<BulkCreateIntelSourcesResponse | null>(null);

  const category = form.watch('category') ?? 'social';
  const handles = form.watch('handles') ?? '';
  const count = parseHandles(handles).length;
  const platformOptions = ALL_PLATFORMS.filter((p) => p.category === category).map((p) => ({
    value: p.slug,
    label: p.name,
  }));

  // Changing the category moves the platform to the first one of that category.
  useEffect(() => {
    const subscription = form.watch((_values, { name }) => {
      if (name !== 'category') return;
      const first = ALL_PLATFORMS.find((p) => p.category === form.getValues('category'));
      if (first) form.setValue('platform', first.slug);
    });
    return () => subscription.unsubscribe();
  }, [form]);

  if (result) {
    return (
      <div className="space-y-4">
        <p className="flex items-center gap-2 text-sm text-success">
          <CheckCircle size={16} aria-hidden="true" />
          {result.created.length} {result.created.length === 1 ? 'source' : 'sources'} imported.
        </p>
        {result.errors.length > 0 && (
          <div className="space-y-2">
            <p className="text-sm font-medium text-danger">{result.errors.length} failed</p>
            <ul className="max-h-40 space-y-1 overflow-auto rounded-lg bg-surface-alt p-3">
              {result.errors.map((e) => (
                <li key={e.handle} className="flex items-center gap-2 text-sm">
                  <span className="font-mono text-xs text-heading">{e.handle}</span>
                  <span className="text-xs text-danger">{e.error}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
        <div className="flex justify-end">
          <Button size="sm" onClick={onDone}>
            Done
          </Button>
        </div>
      </div>
    );
  }

  return (
    <Form
      form={form}
      className="gap-4"
      onSubmit={async (values) => {
        const res = await mutation.mutateAsync({
          body: {
            items: parseHandles(values.handles).map((handle) => ({
              platform: values.platform,
              handle,
              category: values.category,
            })),
          },
        });
        setResult(res);
        if (res.created.length > 0) onImported();
      }}
    >
      <p className="text-sm text-muted">Paste a list of handles or addresses, one per line.</p>
      <SelectFormField name="category" label="Category" options={CATEGORY_OPTIONS} required />
      <SelectFormField name="platform" label="Platform" options={platformOptions} required />
      <TextAreaFormField
        name="handles"
        label="Handles or addresses"
        placeholder={'@user1\n@user2\n0xabc123...'}
        description={`${count} ${count === 1 ? 'item' : 'items'} detected`}
        required
      />
      <div className="flex justify-end">
        <SubmitButton variant="primary" size="md" isDisabled={count === 0}>
          <Upload size={14} aria-hidden="true" /> Import {count} {count === 1 ? 'source' : 'sources'}
        </SubmitButton>
      </div>
    </Form>
  );
}

export const BulkImportModal: React.FC<BulkImportModalProps> = ({ open, onClose, onImported }) => (
  <Modal open={open} onClose={onClose} title="Import a list of sources" maxWidth="lg">
    {/* Remounts per opening so the form starts empty. */}
    <ImportForm key={String(open)} onImported={onImported} onDone={onClose} />
  </Modal>
);
